import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';

import { Culture } from '@/dto';
import { UserState } from '.';
import container from '@/container';
import { FetchServiceContract, CONTRACTS } from '@/core/contracts';
import culture from '@/store/culture';

/**
 * Culture module state
 */
export interface CultureState {
    /** All loaded from the backend cultures */
    cultures: Culture[];
    /** A culture of current user */
    currentCulture: Culture | undefined;
}

/**
 * Culture module implementation
 */
@Module({ name: 'culture', namespaced: true })
export default class CultureModule extends VuexModule
    implements CultureState {

    /** State of culture loading */
    private _isLoaded: boolean = false;
    /** Loaded cultures */
    private _cultures: Culture[] = [];

    /** Loaded from the backend cultures */
    get cultures() {
        return this._cultures;
    }
    /** A culture of current user */
    get currentCulture() {
        let result;

        const userLocalName = UserState.current!.culture;
        if (!userLocalName) {
            result = undefined;
        } else {
            const idx = this._cultures
                .findIndex(x => x.localName === userLocalName);
            if (idx < 0) {
                result = undefined;
            } else {
                result = this._cultures[idx];
            }
        }

        return result;
    }

    /**
     * When it is necessary loads cultures from the backend
     */
    @Action
    public async ensureLoaded(): Promise<void> {
        if (!this._isLoaded) {
            await this.load();
        }
    }

    /**
     * Changes state - set cultures collection
     * @param { Culture[] } cultures New collection of cultures
     */
    @Mutation
    private setCultures(cultures: Culture[]): void {
        // TODO Add some kind of runtime validation?
        this._cultures = cultures;
    }
    /**
     * Changes state - set flag of cultures loaded
     */
    @Mutation
    private setIsLoaded(isLoaded: boolean): void {
        this._isLoaded = isLoaded;
    }
    /**
     * Loads cultures from the backend
     */
    @Action
    private async load(): Promise<void> {
        this.setIsLoaded(false);
        try {
            const fetchService = container
                .get<FetchServiceContract>(CONTRACTS.FetchService);

            const cultures = await fetchService
                .get<Culture[]>('/cultures');

            this.setCultures(cultures);
        } finally {
            this.setIsLoaded(true);
        }
    }
}
